import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

export interface HoverInfo {
  courseId: string;
  courseCardPosition: DOMRect;
}

interface HoverInfoContext {
  hoverInfo: HoverInfo | null;
  popupRef: React.RefObject<HTMLDivElement>;
  handleMouseEnter: (data: HoverInfo) => void;
  handleMouseLeave: (id?: string) => void;
}

interface SidebarProviderProps {
  children: React.ReactNode;
}

const SidebarContext = createContext<HoverInfoContext | undefined>(undefined);

export const useSidebarContext = (): HoverInfoContext => {
  const context = useContext(SidebarContext);

  if (!context) {
    throw new Error('useSidebarContext must be used within a SidebarContextProvider');
  }

  return context;
};

export const SidebarProvider: React.FC<SidebarProviderProps> = ({ children }) => {
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);

  const popupRef = useRef<HTMLDivElement>(null);
  const lastIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback((data: HoverInfo) => {
    clear();

    if (lastIdRef.current !== data.courseId) {
      setHoverInfo(null);
    }

    lastIdRef.current = data.courseId;

    timeoutRef.current = setTimeout(() => {
      if (lastIdRef.current === data.courseId) {
        setHoverInfo(data);
      }
    }, 1000);
  }, []);

  const handleMouseLeave = useCallback((id?: string) => {
    clear();

    if (!id || lastIdRef.current === id) {
      lastIdRef.current = null;
      setHoverInfo(null);
    }
  }, []);

  const contextValue = useMemo(
    () => ({ hoverInfo, handleMouseEnter, handleMouseLeave, popupRef }),
    [hoverInfo, handleMouseEnter, handleMouseLeave],
  );

  return <SidebarContext.Provider value={contextValue}>{children}</SidebarContext.Provider>;
};
